import React from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import { DEMAND_COLOR } from "@/lib/planModel";
import MacroChart from "@/components/wizard/MacroChart";

// Macro colours pulled from CSS custom properties so light and dark can
// tune independently — no more inline neon hexes bleeding through.
const MACRO = { protein: "var(--macro-protein)", carb: "var(--macro-carb)", fat: "var(--macro-fat)" };
const BAR_NEUTRAL = "var(--macro-protein)";
const AXIS = "#d4d4d8";
const tooltipStyle = { background: "#141414", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 0, color: "#fff", fontSize: 12 };

const toData = (daily) => daily.map((d) => ({
  day: d.day, energy: d.energy, protein: d.protein, carb: d.carb, fat: d.fat,
  training: d.trainingMinutes, demand: d.demand,
  hydration: Number(((d.hydrationMl || 0) / 1000).toFixed(2)),
}));

function Panel({ title, children, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#141414] p-4 print-day">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-4">{title}</p>
      {children}
    </div>
  );
}

// Full chart set for the weekly Summary page.
export function WeeklyCharts({ daily, weekAverage }) {
  if (!daily || !daily.length) return null;
  const data = toData(daily);

  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid="weekly-charts">
      {/* Energy + training load */}
      <Panel title="Daily Energy & Training Load" testId="chart-energy-load">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
            <YAxis yAxisId="l" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="r" orientation="right" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit="m" />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }}
              formatter={(v, n) => n === "energy" ? [`${v} kcal`, "Energy"] : [`${v} min`, "Training"]} />
            <Bar yAxisId="l" dataKey="energy" radius={[2, 2, 0, 0]} maxBarSize={34}>
              {data.map((d) => <Cell key={d.day} fill={DEMAND_COLOR[d.demand] || "#D4A574"} />)}
            </Bar>
            <Line yAxisId="r" type="monotone" dataKey="training" stroke="#E2E30A" strokeWidth={2} dot={{ r: 3, fill: "#E2E30A" }} />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-zinc-300 mt-2">Bars = daily energy (warm scale = harder day) · Yellow line = training minutes.</p>
      </Panel>

      {/* Macro grams stacked per day */}
      <Panel title="Daily Macros (grams)" testId="chart-macros-daily">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
            <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit="g" />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v, n) => [`${v}g`, n[0].toUpperCase() + n.slice(1)]} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#d4d4d8" }} iconType="circle" />
            <Bar dataKey="protein" stackId="m" fill={MACRO.protein} maxBarSize={34} />
            <Bar dataKey="carb" stackId="m" fill={MACRO.carb} maxBarSize={34} />
            <Bar dataKey="fat" stackId="m" fill={MACRO.fat} radius={[2, 2, 0, 0]} maxBarSize={34} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>

      {/* Week average macro split */}
      {weekAverage && (
        <Panel title="Week Average Macro Split" testId="chart-macro-split">
          <div className="flex items-center gap-6 justify-center py-2">
            <MacroChart protein={weekAverage.protein} carb={weekAverage.carb} fat={weekAverage.fat} size={180} />
            <div className="space-y-2">
              {[["Protein", weekAverage.protein, MACRO.protein], ["Carbs", weekAverage.carb, MACRO.carb], ["Fat", weekAverage.fat, MACRO.fat]].map(([label, g, c]) => (
                <div key={label} className="flex items-center gap-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
                  <span className="text-zinc-200 w-16">{label}</span>
                  <span className="font-mono text-white">{g}g</span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      )}

      {/* Carbohydrate per day */}
      <Panel title="Daily Carbohydrate (fuel)" testId="chart-carb-trend">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
            <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit="g" />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => [`${v}g`, "Carbs"]} />
            <Bar dataKey="carb" radius={[2, 2, 0, 0]} maxBarSize={34}>
              {data.map((d) => <Cell key={d.day} fill={DEMAND_COLOR[d.demand] || "#D4A574"} />)}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-zinc-200 mt-2">Carb intake scales with training demand — more fuel on harder days.</p>
      </Panel>

      {/* Hydration per day */}
      <Panel title="Daily Hydration (L)" testId="chart-hydration">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
            <YAxis tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit="L" />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => [`${v} L`, "Hydration"]} />
            <Bar dataKey="hydration" radius={[2, 2, 0, 0]} maxBarSize={34} fill={BAR_NEUTRAL} />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-zinc-200 mt-2">Daily fluid target rises with training minutes and hot conditions.</p>
      </Panel>
    </div>
  );
}

// Compact single chart for the Plan step.
export function EnergyLoadChart({ daily }) {
  if (!daily || !daily.length) return null;
  const data = toData(daily);
  return (
    <div className="border border-white/10 bg-[#141414] p-4 mb-6" data-testid="chart-energy-load-compact">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-4">Week at a Glance — Energy & Training Load</p>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="day" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
          <YAxis yAxisId="l" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis yAxisId="r" orientation="right" tick={{ fill: AXIS, fontSize: 11 }} tickLine={false} axisLine={false} unit="m" />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }}
            formatter={(v, n) => n === "energy" ? [`${v} kcal`, "Energy"] : [`${v} min`, "Training"]} />
          <Bar yAxisId="l" dataKey="energy" radius={[2, 2, 0, 0]} maxBarSize={34}>
            {data.map((d, i) => <Cell key={i} fill={DEMAND_COLOR[d.demand] || "#D4A574"} />)}
          </Bar>
          <Line yAxisId="r" type="monotone" dataKey="training" stroke="#E2E30A" strokeWidth={2} dot={{ r: 3, fill: "#E2E30A" }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
